'use client'

import {action, computed, makeObservable, observable} from 'mobx'
import type {TransformFiltersUseCase} from '@/src/core/application/catalog'
import type {Filter} from '../types'
import type {FiltersViewData, FiltersViewModel} from './filters.view-model.interface'
import type {ParsedFilterQuery} from '../utils/filter-url.utils'
import {
  applySelectedStateToFilters,
  deriveSelectedFilters,
  NON_PROPERTY_FILTERS,
  serializeFiltersToQuery,
} from '../utils/filter-url.utils'
import type {CatalogSection} from '@/src/core/domain/entities/catalog'

export class CsrFiltersStore implements FiltersViewModel {
  @observable
  private transformFiltersUseCase!: TransformFiltersUseCase

  @observable
  private _rawFilters: Filter[] = []

  @observable
  private _subsections: CatalogSection[] = []

  constructor(filters: Filter[], transformFiltersUseCase: TransformFiltersUseCase) {
    this._rawFilters = filters
    this.transformFiltersUseCase = transformFiltersUseCase
    makeObservable(this)
  }

  @computed
  get selectedCount(): number {
    return this.data.selectedFilters.length
  }

  @computed
  get data(): FiltersViewData {
    return {
      filters: this.transformFiltersUseCase.execute(this._rawFilters as any, this._subsections),
      selectedFilters: deriveSelectedFilters(this._rawFilters),
    }
  }

  @action
  initialize(rawFilters: Filter[], subsections: CatalogSection[] = []) {
    this._subsections = subsections
    this._rawFilters = rawFilters
  }

  @action.bound
  setFilterValue(code: string, value: unknown) {
    const isProperty = !NON_PROPERTY_FILTERS.includes(code)

    const serialized = serializeFiltersToQuery(this._rawFilters)

    const properties = (serialized.properties as Record<string, unknown>) ?? {}
    const updated: Record<string, unknown> = {...serialized}

    if (isProperty) {
      updated.properties = {...properties, [code]: value}
    } else {
      updated[code] = value
    }

    this._rawFilters = applySelectedStateToFilters(
      this._rawFilters,
      this._parseSerializedQuery(updated),
    )
  }

  @action.bound
  removeAllFilters() {
    this._rawFilters = applySelectedStateToFilters(this._rawFilters, {properties: {}, ranges: {}})
  }

  @action.bound
  removeFilterValue(code: string, value: string) {
    const isProperty = !NON_PROPERTY_FILTERS.includes(code)
    const serialized = serializeFiltersToQuery(this._rawFilters)
    const updated: Record<string, unknown> = {...serialized}

    if (isProperty) {
      const properties = (serialized.properties as Record<string, unknown>) ?? {}
      const currentValues = (properties[code] as string[]) ?? []
      const next = currentValues.filter(v => v !== value)
      updated.properties = next.length > 0 ? {...properties, [code]: next} : {...properties}
      if (next.length === 0) {
        delete (updated.properties as Record<string, unknown>)[code]
      }
    } else {
      delete updated[code]
    }

    this._rawFilters = applySelectedStateToFilters(
      this._rawFilters,
      this._parseSerializedQuery(updated),
    )
  }

  private _buildParsedQueryFromCurrentState(): ParsedFilterQuery {
    return this._parseSerializedQuery(serializeFiltersToQuery(this._rawFilters))
  }

  private _parseSerializedQuery(serialized: Record<string, unknown>): ParsedFilterQuery {
    const result: ParsedFilterQuery = {properties: {}, ranges: {}}
    const properties = serialized.properties as Record<string, unknown> | undefined

    if (properties) {
      for (const [code, value] of Object.entries(properties)) {
        if (Array.isArray(value)) {
          result.properties[code] = value.map(String)
        }
      }
    }

    for (const [code, value] of Object.entries(serialized)) {
      if (code === 'properties') continue
      if (
        typeof value === 'object' &&
        value !== null &&
        !Array.isArray(value) &&
        ('from' in value || 'to' in value)
      ) {
        const obj = value as Record<string, unknown>
        result.ranges[code] = {from: Number(obj['from'] ?? 0), to: Number(obj['to'] ?? 0)}
      }
    }

    return result
  }
}
